import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Trash2, Save, Sparkles, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtOdds, pct, money, parlayMath, SPORT_META, TIER_LIMITS, stakeSuggestion } from "@/lib/odds";
import { useSlip } from "@/context/SlipContext";
import { useAuth } from "@/context/AuthContext";
import { Wallet } from "lucide-react";
import { AltLineLadder } from "@/components/AltLineLadder";
import { PlayerLink } from "@/components/PlayerLink";
import { SlipInsights } from "@/components/SlipInsights";
import { SlipShareModal } from "@/components/SlipShareCard";

export function ParlaySlip({ full = false }) {
  const { legs, wager, setWager, remove, clear, meta } = useSlip();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [shareCrew, setShareCrew] = useState(user?.auto_share_crew !== false);
  const m = parlayMath(legs, Number(wager) || 0);
  const maxLegs = TIER_LIMITS[user?.tier]?.maxLegs ?? 0;
  const canSave = legs.length >= 2 && maxLegs > 0 && legs.length <= maxLegs;
  const hasLocked = legs.some((l) => l.win_probability == null);
  const suggestion = legs.length >= 2 && !hasLocked ? stakeSuggestion(user?.bankroll, m.decimal, m.prob) : null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/parlays", { pick_ids: legs.map((l) => l.pick_id), wager: Number(wager), title: title || meta?.title || undefined, ai_optimized: !!meta, share_to_crew: shareCrew });
      toast.success(data.crew_reach ? `Parlay saved and posted to ${data.crew_reach} crew member${data.crew_reach > 1 ? "s" : ""}` : "Parlay saved to your lab");
      clear();
      setTitle("");
      navigate("/parlays");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside data-testid="parlay-builder-container" className="glass-card flex flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-[#26334D] px-5 py-4">
        <h2 className="font-display flex items-center gap-2 text-2xl font-bold uppercase tracking-tight text-white"><Ticket size={20} className="text-emerald-400" /> Parlay Slip</h2>
        <span data-testid="parlay-leg-count" className="font-mono-num rounded-full bg-[#0B0E14] px-2.5 py-0.5 text-xs text-slate-300">{legs.length} legs</span>
      </div>

      {meta && (
        <div data-testid="parlay-ai-strategy" className="border-b border-[#26334D] bg-cyan-500/5 px-5 py-3">
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-cyan-400"><Sparkles size={12} /> {meta.title}</p>
          <p className="mt-1 text-xs leading-relaxed text-slate-300">{meta.strategy}</p>
        </div>
      )}

      <div className="max-h-[42vh] overflow-y-auto">
        {legs.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-slate-500" data-testid="parlay-empty">Add picks from the daily slate to start building.</p>
        ) : (
          legs.map((l) => (
            <div key={l.pick_id} data-testid="parlay-leg-item" className="flex items-center gap-3 border-b border-[#26334D]/60 px-5 py-3 transition-colors hover:bg-[#182030]">
              <span className="h-8 w-1 rounded-full" style={{ background: SPORT_META[l.sport].color }} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs text-slate-500">{l.matchup}</p>
                <p className="truncate text-sm font-semibold text-white">{l.market === "Alt Line" && <span className="mr-1 rounded bg-fuchsia-500/20 px-1 text-[9px] font-bold text-fuchsia-300">ALT</span>}<PlayerLink player={l.player} label={l.selection} /></p>
              </div>
              <AltLineLadder leg={l} />
              <span className="font-mono-num text-sm font-bold text-amber-400">{fmtOdds(l.odds)}</span>
              <span className="font-mono-num w-10 text-right text-xs text-emerald-400">{pct(l.win_probability)}</span>
              <button data-testid="parlay-remove-leg-btn" onClick={() => remove(l.pick_id)} className="rounded p-1 text-slate-500 transition-colors hover:bg-red-500/15 hover:text-red-400"><X size={14} /></button>
            </div>
          ))
        )}
      </div>

      <SlipInsights />
      <div className="space-y-3 bg-[#0B0E14]/60 px-5 py-4">
        <div className="flex items-center gap-3">
          <label className="label-caps shrink-0">Wager</label>
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
            <input data-testid="parlay-wager-input" type="number" min="1" value={wager} onChange={(e) => setWager(e.target.value)} className="input-dark font-mono-num !pl-7" />
          </div>
        </div>
        {full && <input data-testid="parlay-title-input" placeholder="Name this parlay (optional)" value={title} onChange={(e) => setTitle(e.target.value)} className="input-dark" />}
        {legs.length >= 2 && !hasLocked && (
          suggestion ? (
            <div data-testid="stake-suggestion" className="flex items-center gap-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5 px-3 py-2">
              <Wallet size={15} className="shrink-0 text-cyan-400" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-400">{suggestion.method}: <span className="font-mono-num font-bold text-white" data-testid="stake-suggestion-amount">{money(suggestion.stake)}</span></p>
                <p className="truncate text-[11px] text-slate-500">{suggestion.label} · EV {suggestion.edge >= 0 ? "+" : ""}{(suggestion.edge * 100).toFixed(1)}%</p>
              </div>
              {suggestion.stake >= 1 && <button data-testid="stake-suggestion-use" onClick={() => setWager(suggestion.stake.toFixed(2))} className="btn-ghost !px-3 !py-1 text-xs">Use</button>}
            </div>
          ) : (
            <p className="text-[11px] text-slate-500" data-testid="stake-suggestion-empty">Set a bankroll on the <button className="text-cyan-400 underline" onClick={() => navigate("/performance")}>Performance</button> page for stake sizing.</p>
          )
        )}
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Total odds" value={legs.length ? fmtOdds(m.american) : "—"} color="text-amber-400" id="parlay-total-odds" />
          <Stat label="Win prob" value={legs.length && !hasLocked ? pct(m.prob) : "—"} color="text-emerald-400" id="parlay-win-prob" />
          <Stat label="Payout" value={legs.length ? money(m.payout) : "—"} color="text-white" id="parlay-potential-payout" />
        </div>
        {legs.length > 0 && maxLegs > 0 && legs.length > maxLegs && (
          <p data-testid="parlay-leg-limit-warning" className="text-xs text-amber-400">Your {user.tier} plan allows up to {maxLegs} legs. Upgrade to Premium for up to 12.</p>
        )}
        {legs.length > 0 && maxLegs === 0 && (
          <p data-testid="parlay-save-locked" className="text-xs text-amber-400">Saving parlays requires Basic or Premium. <button className="underline" onClick={() => navigate("/pricing")}>Upgrade</button></p>
        )}
        {legs.length >= 2 && maxLegs > 0 && (
          <label className="flex cursor-pointer items-center gap-2 text-[11px] text-slate-400" data-testid="slip-crew-share-label">
            <input type="checkbox" data-testid="slip-crew-share-toggle" checked={shareCrew} onChange={async (e) => { setShareCrew(e.target.checked); try { const { data } = await api.patch("/users/me/preferences", { auto_share_crew: e.target.checked }); setUser(data); } catch {} }} className="h-3.5 w-3.5 accent-emerald-500" />
            Post the slip card to my crew when I save{user?.share_tickets === false && <span className="text-amber-400"> (tickets are private — turn on sharing in the bell menu)</span>}
          </label>
        )}
        <div className="flex gap-2">
          <button data-testid="parlay-clear-btn" onClick={clear} disabled={!legs.length} className="btn-ghost !px-3"><Trash2 size={15} /></button>
          <SlipShareModal legs={legs} wager={wager} meta={meta} />
          <button data-testid="parlay-save-btn" onClick={save} disabled={!canSave || saving} className="btn-primary flex-1"><Save size={15} /> {saving ? "Saving…" : "Save Parlay"}</button>
        </div>
      </div>
    </aside>
  );
}

function Stat({ label, value, color, id }) {
  return (
    <div className="rounded-lg border border-[#26334D] bg-[#121824] px-3 py-2">
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p data-testid={id} className={`font-mono-num truncate text-base font-bold ${color}`}>{value}</p>
    </div>
  );
}
